import React, { useEffect, useState } from "react";
import { employees, getEmployeeName } from "../../Config/Employees";
import {
  Box,
  Button,
  Badge,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { DateTime } from "luxon";
import EmployeeDataService from "../../services/Employee";
import { getUserAccess } from "../../utils/authUtils";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";

export const EmployeeTasks = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const user = localStorage.getItem("logged_in_email") || "";
  const { isAdmin } = getUserAccess();
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [tasks, setTasks] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [taskDate, setTaskDate] = useState(DateTime.now().toFormat("yyyy-MM-dd"));
  const [taskList, setTaskList] = useState([
    { taskName: "", description: "", status: "pending" },
  ]);

  // Filter state
  const [filterDate, setFilterDate] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterEmail, setFilterEmail] = useState("");


  const generateMonthYearArray = () => {
    const startDate = DateTime.fromObject({ month: 11, year: 2025 });
    const endDate = DateTime.now();
    let currentDate = startDate;
    const arr: any[] = [];
    while (currentDate <= endDate) {
      arr.push({
        month: currentDate.toFormat("MM"),
        year: currentDate.year.toString(),
        text: `${currentDate.toFormat("MMMM")}-${currentDate.year}`,
      });
      currentDate = currentDate.plus({ months: 1 });
    }
    return arr;
  };

  const getStatusBadge = (status: string) => {
    const colorMap: any = {
      pending: "gray",
      in_progress: "blue",
      completed: "green",
    };
    return (
      <Badge colorScheme={colorMap[status] || "gray"}>
        {status?.replace(/_/g, " ")}
      </Badge>
    );
  };

  const fetchTasks = async () => {
    setIsLoading(true);
    try {
      const res = await EmployeeDataService.getTasks(
        token,
        user,
        isAdmin ? filterEmail : "",
        filterDate,
        filterMonth,
        filterYear
      );
      if (res.data?.data) {
        setTasks(res.data.data);
      }
    } catch (e: any) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const addTaskRow = () => {
    setTaskList([...taskList, { taskName: "", description: "", status: "pending" }]);
  };

  const removeTaskRow = (index: number) => {
    if (taskList.length === 1) return;
    setTaskList(taskList.filter((_, i) => i !== index));
  };

  const updateTaskRow = (index: number, field: string, value: string) => {
    const updated = [...taskList];
    (updated[index] as any)[field] = value;
    setTaskList(updated);
  };

  const handleSubmitTasks = async () => {
    const validTasks = taskList.filter((t) => t.taskName.trim());
    if (validTasks.length === 0) {
      toast({
        position: "top",
        title: "Please add at least one task",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const data = {
        email: userEmail,
        name: getEmployeeName(userEmail),
        date: taskDate,
        tasks: validTasks,
      };
      await EmployeeDataService.createTask(token, data);
      toast({
        position: "top",
        title: "Tasks logged successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      setTaskList([{ taskName: "", description: "", status: "pending" }]);
      fetchTasks();
    } catch (e: any) {
      toast({
        position: "top",
        title: "Failed to log tasks",
        description: e.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusUpdate = async (taskId: string, taskIndex: number, newStatus: string) => {
    try {
      await EmployeeDataService.updateTask(token, taskId, { status: newStatus, taskIndex });
      toast({
        position: "top",
        title: "Status updated",
        status: "success",
        duration: 1500,
        isClosable: true,
      });
      fetchTasks();
    } catch (e: any) {
      toast({
        position: "top",
        title: "Update failed",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  return (
    <HrmsPageShell title="My Tasks">
      <>
        {/* Log Today's Tasks Form */}
        {!isAdmin && (
          <Box bg="white" borderWidth="1px" borderRadius="md" p={6} mb={6}>
            <Text fontWeight="bold" fontSize="md" mb={4}>
              Tasks
            </Text>

            <FormControl mb={4} w="200px">
              <FormLabel fontSize="sm">Date</FormLabel>
              <Input
                type="date"
                value={taskDate}
                onChange={(e) => setTaskDate(e.target.value)}
              />
            </FormControl>

            {taskList.map((task, index) => (
              <Flex key={index} gap={3} mb={3} align="flex-end" flexWrap="wrap">
                <FormControl w="200px">
                  {index === 0 && <FormLabel fontSize="sm">Task Name</FormLabel>}
                  <Input
                    placeholder="Task name"
                    value={task.taskName}
                    onChange={(e) => updateTaskRow(index, "taskName", e.target.value)}
                  />
                </FormControl>
                <FormControl w="300px">
                  {index === 0 && <FormLabel fontSize="sm">Description</FormLabel>}
                  <Input
                    placeholder="Description"
                    value={task.description}
                    onChange={(e) => updateTaskRow(index, "description", e.target.value)}
                  />
                </FormControl>
                <FormControl w="150px">
                  {index === 0 && <FormLabel fontSize="sm">Status</FormLabel>}
                  <Select
                    value={task.status}
                    onChange={(e) => updateTaskRow(index, "status", e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </Select>
                </FormControl>
                <IconButton
                  aria-label="Remove task"
                  icon={<DeleteIcon />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => removeTaskRow(index)}
                  isDisabled={taskList.length === 1}
                />
              </Flex>
            ))}

            <Flex gap={3} mt={4}>
              <Button
                size="sm"
                leftIcon={<AddIcon />}
                variant="outline"
                colorScheme="teal"
                onClick={addTaskRow}
              >
                Add Task
              </Button>
              <Button
                bg="#327171"
                color="white"
                size="sm"
                onClick={handleSubmitTasks}
                isLoading={isSubmitting}
                _hover={{ bg: "#285e5e" }}
              >
                Submit Tasks
              </Button>
            </Flex>
          </Box>
        )}

        {/* Filters */}
        <Flex p={4} alignItems="start" justifyContent="end" gap={8} flexWrap="wrap" mb={4}>
          {isAdmin && (
            <Flex display="flex" alignItems="flex-end" w="300px">
              <Select
                placeholder="Select Employee"
                value={filterEmail}
                onChange={(e) => setFilterEmail(e.target.value)}
                color="#327171"
                borderRightRadius={0}
                focusBorderColor="teal"
                borderWidth="0.2px"
                _focus={{ borderWidth: "0.2px", borderColor: "teal.200" }}
              >
                {employees.map((emp) => (
                  <option key={emp.value} value={emp.value}>
                    {emp.label}
                  </option>
                ))}
              </Select>
              <Button
                size="md"
                borderRadius="md"
                borderLeftRadius={0}
                p={5}
                colorScheme="whiteAlpha"
                bg="#327171"
                onClick={fetchTasks}
              >
                Search
              </Button>
            </Flex>
          )}

          <Flex display="flex" alignItems="flex-end" w="280px">
            <Input
              value={filterDate}
              onChange={(e) => {
                setFilterDate(e.target.value);
                setFilterMonth("");
                setFilterYear("");
              }}
              type="date"
              placeholder="Search By Date"
              borderRadius="md"
              borderRightRadius={0}
              borderWidth="1px"
              color="#327171"
              bg="white"
              focusBorderColor="transparent"
              _hover={{ borderColor: "gray.200" }}
              _focus={{ borderColor: "gray.200" }}
            />
            <Button
              size="md"
              borderRadius="md"
              borderLeftRadius={0}
              py={5}
              px={8}
              colorScheme="whiteAlpha"
              bg="#327171"
              onClick={fetchTasks}
            >
              Search Date
            </Button>
          </Flex>

          <Flex display="flex" alignItems="flex-end" w="300px">
            <Select
              placeholder="Select Month"
              value={filterMonth ? `${filterMonth}-${filterYear}` : ""}
              onChange={(e) => {
                if (e.target.value) {
                  const [m, y] = e.target.value.split("-");
                  setFilterMonth(m);
                  setFilterYear(y);
                  setFilterDate("");
                } else {
                  setFilterMonth("");
                  setFilterYear("");
                }
              }}
              color="#327171"
              borderRightRadius={0}
              focusBorderColor="teal"
              borderWidth="0.2px"
              _focus={{ borderWidth: "0.2px", borderColor: "teal.200" }}
            >
              {generateMonthYearArray().map((el) => (
                <option key={`${el.month}-${el.year}`} value={`${el.month}-${el.year}`}>
                  {el.text}
                </option>
              ))}
            </Select>
            <Button
              size="md"
              borderRadius="md"
              borderLeftRadius={0}
              p={5}
              colorScheme="whiteAlpha"
              bg="#327171"
              onClick={fetchTasks}
            >
              Search
            </Button>
          </Flex>
        </Flex>

        {/* Tasks Table */}
        {isLoading ? (
          <Flex justify="center" py={10}>
            <Spinner size="xl" color="teal" />
          </Flex>
        ) : (
          <Box bg="white" borderWidth="1px" borderRadius="md" p={4}>
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    {isAdmin && <Th>Employee</Th>}
                    <Th>Date</Th>
                    <Th>Task Name</Th>
                    <Th>Description</Th>
                    <Th>Status</Th>
                    {!isAdmin && <Th>Update</Th>}
                  </Tr>
                </Thead>
                <Tbody>
                  {tasks.length > 0 ? (
                    tasks.map((t: any) =>
                      (t.tasks || [t]).map((task: any, idx: number) => (
                        <Tr key={`${t._id}-${idx}`}>
                          {isAdmin && <Td>{t.name || t.email}</Td>}
                          <Td>{t.date ? DateTime.fromISO(t.date).toFormat("dd MMM yyyy") : "-"}</Td>
                          <Td>{task.taskName}</Td>
                          <Td>{task.description || "-"}</Td>
                          <Td>{getStatusBadge(task.status)}</Td>
                          {!isAdmin && (
                            <Td>
                              <Select
                                size="xs"
                                w="120px"
                                value={task.status}
                                onChange={(e) => handleStatusUpdate(t._id, idx, e.target.value)}
                              >
                                <option value="pending">Pending</option>
                                <option value="in_progress">In Progress</option>
                                <option value="completed">Completed</option>
                              </Select>
                            </Td>
                          )}
                        </Tr>
                      ))
                    )
                  ) : (
                    <Tr>
                      <Td colSpan={isAdmin ? 5 : 6} textAlign="center" color="gray.500">
                        No tasks found
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          </Box>
        )}
      </>
    </HrmsPageShell>
  );
};
